/**
 * The grid behind bar-line notation: "| C |   | B |  |   | B | G |".
 *
 * A chart like that is already meaningful to the app — countBars reads it, and estimatePartBars
 * uses that count as the exact length of a chords-only instrumental instead of guessing from the
 * chords. This module is the other direction: a grid a person can edit, and the text it turns
 * into. The two have to agree exactly, which is what the round-trip test pins down.
 *
 * A grid is `string[][]`: lines of cells, one cell per bar. An empty cell is a bar with no change
 * written in it — the chart saying "hold it" — and it is the same empty segment countBars counts.
 * A cell can hold more than one chord ("B A C"), which is several changes sharing a bar.
 *
 * A grid can also be divided into *sections*: runs of whole lines belonging to one instrument, so a
 * part assigned two of them can write both into a single chart instead of two charts that would
 * share no columns. See sectionsFromBreaks for how they are stored and what they are counted from.
 */

import { findChords, isLyricsLine } from './chordPatterns';

export const BAR = '|';

/**
 * One chord token, anchored.
 *
 * DISPLAY_CHORD_REGEX scans a line for chords and can't say whether a token is *entirely* a chord,
 * which is what deciding "is this line a chart or a lyric that happens to contain a pipe" needs.
 * The body is deliberately DISPLAY_CHORD_REGEX's, minus its trailing context lookaheads — anchors
 * do that job here. A test keeps the two in step.
 *
 * A bass note and an inversion mark are independent, and a symbol can carry both: '/E' chooses the
 * note under the chord, '^1' chooses how the chord above it sits. 'C/E^1' is a low E under a
 * turned-over C, and each mark means the same thing whether or not the other one is there.
 */
const CHORD_TOKEN = /^[A-G](?:#|b)?(?:m|min)?(?:sus|aug|dim|add|M)?\d*(?:\/[A-G](?:#|b)?)?(?:\^[1-3])?$/;

export const isChordToken = (token) => CHORD_TOKEN.test(token);

/**
 * One beat of a bar, written the way every chart writes it.
 *
 * A bar is a row of beats, and a chord claims the beat it is written on and holds until the next
 * chord: "C / / G" is C for three beats and G for one. Without them a bar can only say how many
 * changes share it and not where they land, which splitBeats then has to guess at evenly — so an
 * uneven bar was simply unwritable.
 *
 * A mark is always a *continuation* — of the chord in front of it, or, when it opens the cell, of
 * whatever was still sounding at the bar line behind it. That second reading is a tie, and it is the
 * only way to say that a chord crosses a bar line partway: "| Am / / G / / F / | / / G G |" is F
 * struck on the last eighth of one bar and held two beats into the next. It is the sub-bar version
 * of the empty cell, which has always meant exactly this for a whole bar at a time.
 *
 * What the old rule was really protecting is that a cell of *nothing but* marks is not notation, and
 * that survives: it keeps isBarLine from reading a line of stray slashes as a chart. The other half
 * of the job moved to isBarLine itself — a chart line may not *open* with a mark, since before the
 * first bar there is nothing to tie to, and "/ Am | G |" is what a hand-typed repeat looks like on a
 * line whose outer bar line was left off, which countManualRepeats reads and must go on reading.
 */
export const BEAT_MARK = '/';

export const isBeatMark = (token) => token === BEAT_MARK;

/**
 * Beats nobody has said anything about yet. Builder-only: a shortened chord leaves them behind,
 * they play as whatever is still sounding, and a chart holding any can't be saved. Written like a
 * chord — the mark, then a beat mark for each slot after — so gaps side by side stay apart.
 */
export const MISSING_MARK = '_';

export const isMissingMark = (token) => token === MISSING_MARK;

/**
 * Silence: nothing sounds until the next chord. Written the way chord charts write it ("no chord"),
 * and extended by beat marks like a chord is. Unlike a tie it has a token of its own, so a bar can
 * open on one.
 */
export const REST_MARK = 'N.C.';

export const isRestMark = (token) => token === REST_MARK;

/**
 * A bar played as a line of single notes rather than chords: the symbols in it are note names.
 * Written as the cell's first token, so it rides along with the bar through every grid edit.
 */
export const MELODY_MARK = '♪';

const cellText = (cell) => String(cell ?? '').trim();

export const isMelodyCell = (cell) => cellText(cell).split(/\s+/)[0] === MELODY_MARK;

// The cell without its melody mark
const cellBody = (cell) => (isMelodyCell(cell) ? cellText(cell).slice(MELODY_MARK.length).trim() : cellText(cell));

/** A cell marked as a melody bar or not. A hold has nothing to play, so it stays unmarked. */
export const setCellMelody = (cell, on) => {
  const body = cellBody(cell);

  return on && body ? `${MELODY_MARK} ${body}` : body;
};

// A rewritten bar keeps the melody mark the one it replaces carried
const keepMelody = (cell, text) => setCellMelody(text, isMelodyCell(cell));

/** A bar's worth of text: empty (a hold), or chords and rests and the beats they are held over. */
const isCellText = (text) => {
  const trimmed = cellBody(text);

  if (trimmed === '') return !isMelodyCell(text);

  const tokens = trimmed.split(/\s+/);

  // A cell made only of marks holds nothing of its own — that is an empty cell, written empty
  return (
    tokens.every((token) => isChordToken(token) || isBeatMark(token) || isRestMark(token)) &&
    tokens.some((token) => isChordToken(token) || isRestMark(token))
  );
};

/**
 * A line written as a chart rather than as words.
 *
 * Strict on purpose: every segment has to be a hold or chords, so an ordinary lyric that happens
 * to contain a pipe is not swallowed into the grid when the builder reopens on a part.
 */
export const isBarLine = (line) => {
  const text = String(line ?? '').trim();

  if (!text.includes(BAR)) return false;

  // Nothing before the first bar to tie to — and this is the shape of a hand-typed repeat on a line
  // that left its outer bar line off, which is the one thing relaxing isCellText would have swallowed
  if (text.startsWith(BEAT_MARK)) return false;

  return text.split(BAR).every(isCellText);
};

/** The cells of one bar line, using countBars' own reading of where the bars are. */
const splitCells = (line) => {
  const segments = String(line).split(BAR);

  // Outer bar lines are optional, exactly as countBars has it — an empty segment at either end is
  // the notation's own edge, not a bar
  if (segments[0].trim() === '') segments.shift();
  if (segments.length > 0 && segments[segments.length - 1].trim() === '') segments.pop();

  return segments.map((segment) => segment.trim().replace(/\s+/g, ' '));
};

// Read through cellChords rather than off the raw text: a cell is non-empty as soon as it carries
// a beat mark, and a line of those is still a line with no chord written on it
const hasChord = (cells) => cells.some((cell) => cellChords(cell).length > 0);

// Chords or rests: a row of rests says something, even though on its own it reads as words
const hasContent = (cells) => hasChord(cells) || cells.some((cell) => parseCell(cell).some(({ rest }) => rest));

/* ---------------------------------------------------------------------------
 * Sections
 *
 * A section is a run of whole chart lines belonging to one instrument, and it is stored as a single
 * blank row between two runs of bar lines. A blank row is invisible to countBars, so a chart that carries sections counts the same number of
 * bars as the one that does not — sections are sequential, and the chart stays one timeline.
 *
 * Two or more blank rows keep their old meaning: a deliberate gap, and two separate charts.
 *
 * In memory sections travel as a `number[]` beside the grid, one entry per line — non-decreasing,
 * gap-free, starting at 0. One entry per line rather than a list of break positions because every
 * edit the builder makes to a line is then the same splice applied to both arrays, which is what
 * stops the two drifting apart.
 * ------------------------------------------------------------------------- */

/**
 * Sections put back in order after an edit.
 *
 * Two lines are in the same section when they were carrying the same value and are still next to
 * each other; anywhere that changes, a break falls. Renumbering from there is what lets every
 * operation below splice raw values around and hand the result here rather than working out what
 * the indices should become — a line keeps the section it belonged to wherever it is dragged, and a
 * section emptied by a deletion closes up.
 */
export const normalizeSections = (sections) => {
  let section = 0;

  return (sections || []).map((value, index) => {
    if (index > 0 && value !== sections[index - 1]) section += 1;

    return section;
  });
};

/** Sections for a grid of `count` lines, from the line indices that open a new one. */
export const sectionsFromBreaks = (breaks, count) => {
  const opens = new Set(breaks);
  let section = 0;

  return Array.from({ length: count }, (_, line) => {
    if (line > 0 && opens.has(line)) section += 1;

    return section;
  });
};

/** Whether a line opens a section of its own — which is to say, whether a break is written above it. */
export const opensSection = (sections, line) => line > 0 && (sections?.[line] ?? 0) > (sections?.[line - 1] ?? 0);

/**
 * The break above a line put in or taken out, which is the whole of what the builder's menu does.
 *
 * Worked in terms of where the breaks fall rather than by editing one entry: the numbers below a
 * changed line are relative to a numbering that no longer holds, and renumbering from them reads
 * the section under a removed break as a new one of its own.
 */
export const toggleSection = (sections, line) => {
  if (line <= 0 || sections?.[line] === undefined) return sections;

  const breaks = sections.map((_, i) => i).filter((i) => opensSection(sections, i));

  return sectionsFromBreaks(
    opensSection(sections, line) ? breaks.filter((i) => i !== line) : [...breaks, line],
    sections.length,
  );
};

/* The line edits below, mirrored onto the sections beside the grid. Each is the same splice its
 * counterpart in "Grid editing" makes, and normalizeSections settles whatever that leaves behind. */

/** A new line joins the section the chart currently ends in. */
export const sectionsAfterAddLine = (sections) => [...(sections || []), sections?.[sections.length - 1] ?? 0];

/**
 * Sections resized to a grid that changed length underneath them.
 *
 * Writing a chord onto the last bar of the last line grows the chart by a line — writeChord adds it
 * rather than truncating the chord — so a line can appear without any of the operations above being
 * the one that made it. Lines gained that way continue the music that ran into them, and so join the
 * section the chart ends in.
 */
export const sectionsForLines = (sections, count) => {
  const current = sections || [];

  if (current.length === count) return current;

  const last = current[current.length - 1] ?? 0;

  return normalizeSections(Array.from({ length: count }, (_, i) => current[i] ?? last));
};

export const sectionsAfterRemoveLine = (sections, line) =>
  normalizeSections((sections || []).filter((_, i) => i !== line));

/** A copy of a line belongs to the section it was copied from. */
export const sectionsAfterDuplicateLine = (sections, line) =>
  normalizeSections([...(sections || []).slice(0, line + 1), sections?.[line] ?? 0, ...(sections || []).slice(line + 1)]);

export const sectionsAfterMoveLine = (sections, from, to) => {
  if (from === to || sections?.[from] === undefined) return sections;

  const rest = sections.filter((_, i) => i !== from);

  return normalizeSections([...rest.slice(0, to), sections[from], ...rest.slice(to)]);
};

/**
 * The chart already written into a part's lyrics, as a grid.
 *
 * Returns the block's line span alongside the grid so an edit can be written back over the same
 * lines rather than appended beside them. Only the first chart is taken: a part with two separate
 * charts is not something the builder can represent, and quietly merging them would lose the gap
 * between them.
 *
 * A single blank row with more chart under it is not such a gap — it is a section break, and the
 * whole of the chart it divides is taken, or reopening the builder on a sectioned chart would edit
 * the first section and write the rest back beside it.
 *
 * @returns {{ lines: string[][], sections: number[], startLine: number, endLine: number } | null}
 */
export const parseBarLines = (lyrics) => {
  const rows = String(lyrics ?? '').split(/\r\n|\r|\n/);
  const startLine = rows.findIndex(isBarLine);

  if (startLine === -1) return null;

  const kept = [rows[startLine]];
  const breaks = [];
  let endLine = startLine;

  while (endLine + 1 < rows.length) {
    if (isBarLine(rows[endLine + 1])) {
      kept.push(rows[endLine + 1]);
      endLine += 1;
    } else if (rows[endLine + 1].trim() === '' && isBarLine(rows[endLine + 2])) {
      breaks.push(kept.length);
      kept.push(rows[endLine + 2]);
      endLine += 2;
    } else break;
  }

  return {
    lines: kept.map(splitCells),
    sections: sectionsFromBreaks(breaks, kept.length),
    startLine,
    endLine,
  };
};

/**
 * A part's lyrics split into what is words and what is chart.
 *
 * parseBarLines takes only the first block, which is what the builder wants — it edits one chart.
 * Rendering wants every block, and the words around them in order, so the player can draw a grid
 * where a grid was written and leave the rest as text.
 *
 * @returns {Array<{ type: 'text', text: string } | { type: 'bars', lines: string[][], sections: number[] }>}
 */
export const splitBarBlocks = (lyrics) => {
  const rows = String(lyrics ?? '').split(/\r\n|\r|\n/);
  const runs = [];

  rows.forEach((row) => {
    const type = isBarLine(row) ? 'bars' : 'text';
    const last = runs[runs.length - 1];

    if (last && last.type === type) last.rows.push(row);
    else runs.push({ type, rows: [row] });
  });

  // A single blank row with chart on both sides is a section break inside one chart rather than the
  // end of it, so the two runs either side of it are one block with a break recorded between them.
  // Anything wider is the gap it has always been, and leaves two blocks.
  const blocks = [];

  runs.forEach((run, index) => {
    const last = blocks[blocks.length - 1];
    const isBreak = run.type === 'text' && run.rows.length === 1 && run.rows[0].trim() === '';

    if (isBreak && last?.type === 'bars' && runs[index + 1]?.type === 'bars') {
      last.breaks.push(last.rows.length);

      return;
    }

    if (run.type === 'bars' && last?.type === 'bars') {
      last.rows.push(...run.rows);

      return;
    }

    blocks.push(run.type === 'bars' ? { type: 'bars', rows: [...run.rows], breaks: [] } : run);
  });

  return blocks
    .map((block, index) => {
      if (block.type === 'bars') {
        return {
          type: 'bars',
          lines: block.rows.map(splitCells),
          sections: sectionsFromBreaks(block.breaks, block.rows.length),
        };
      }

      // A grid is drawn as a block and breaks the line by itself, so the blank row that separated it
      // from the words around it would render as an extra empty line. One row goes on each side
      // that touches a chart; a deliberately wider gap keeps the rest.
      const kept = [...block.rows];

      if (blocks[index - 1] && kept[0].trim() === '') kept.shift();
      if (blocks[index + 1] && kept.length > 0 && kept[kept.length - 1].trim() === '') kept.pop();

      return { type: 'text', text: kept.join('\n') };
    })
    .filter((segment) => segment.type === 'bars' || segment.text !== '');
};

/** Every chord the grid strikes, in order — ties and empty bars carry no symbol and count as none. */
export const getChartChords = (lines) => (lines || []).flat().flatMap(cellChords);

/**
 * Whether a chart's chords are the chords written over a part's words, in the same order.
 *
 * A chart kept off the lyrics is usually the lyrics' own chords, imported — so the nth chord the
 * chart plays is the nth chord on the page, and lighting one can light the other. That only holds
 * while the two lists are identical, and a chart edited after it was imported can leave them not
 * to be. This is the question asked before offering to follow the chart through the words at all;
 * the answer being no takes the offer away rather than lighting the wrong syllable.
 *
 * The lyrics are read with findChords rather than the stricter isChordToken: findChords is the very
 * pass replaceChords marks the page up with, so matching it is what makes the nth chord here and the
 * nth chord on screen the same chord.
 */
export const lyricsMatchChart = (lines, lyrics) => {
  const chart = getChartChords(lines);
  const written = findChords(lyrics, { display: true });

  return chart.length > 0 && chart.length === written.length && chart.every((chord, i) => chord === written[i]);
};

/**
 * The grid as it will actually be written.
 *
 * Two things are fixed here rather than left to the caller, because both change what the rest of
 * the app makes of the text:
 *
 * - A line with no chord anywhere in it is merged into its neighbour. On its own such a line is
 *   non-empty text that findChords sees nothing in, which is precisely isLyricsLine's definition —
 *   so estimatePartBars would count a row of held bars as a sung line and time the part wrong.
 * - A line with no cells at all is dropped, so an empty row in the editor writes nothing.
 *
 * Neither merge crosses a section break: the line above one belongs to a different instrument, and
 * a row of holds joined onto it would be that instrument holding a chord the other one played.
 * Whatever the merges leave behind — a line gone, a section emptied — normalizeSections closes up.
 *
 * @returns {{ lines: string[][], sections: number[] }}
 */
export const normalizeGrid = (lines, sections) => {
  const rows = [];

  (lines || []).forEach((cells, index) => {
    const trimmed = (cells || []).map((cell) => String(cell ?? '').trim());

    if (trimmed.length > 0) rows.push({ cells: trimmed, section: sections?.[index] ?? 0 });
  });

  const normalized = [];

  rows.forEach((row) => {
    const last = normalized[normalized.length - 1];

    // Held bars belong to the chord they are holding, which is on the line before
    if (!hasChord(row.cells) && last && last.section === row.section) {
      last.cells = last.cells.concat(row.cells);

      return;
    }

    normalized.push({ ...row });
  });

  // A leading run of holds has nothing behind it to join, so it joins what follows instead
  while (normalized.length > 1 && !hasChord(normalized[0].cells) && normalized[0].section === normalized[1].section) {
    normalized[1].cells = normalized[0].cells.concat(normalized[1].cells);
    normalized.shift();
  }

  // Anything still holding nothing opens a section and had nowhere to go. A section with no chord
  // or rest written in it is not notation, so it goes rather than printing as a row of empty bars.
  const kept = normalized.filter((row) => hasContent(row.cells));

  return {
    lines: kept.map((row) => row.cells),
    sections: normalizeSections(kept.map((row) => row.section)),
  };
};

/**
 * The grid as text: "| C   |     | B   |".
 *
 * Cells are padded to the widest chord in the whole grid so the bars line up as columns. It is
 * cosmetic — countBars is whitespace-blind — but lyrics render in a proportional font, and equal
 * character counts are the closest thing to alignment available there.
 *
 * Every cell goes out through formatCell, which is what keeps the beat marks off the bars that
 * don't need them: a bar the builder left part-written is closed up, and one whose changes fall
 * where splitBeats would have put them anyway is written as the bare chords it has always been.
 * `beatsPerBar` is what that judgement is made against — without one no bar can be called uneven,
 * so the marks are left exactly as they were found.
 *
 * A section break goes out as the blank row it is stored as. Cells are still padded to the widest
 * chord in the *whole* grid rather than within each section, so the columns of one instrument's
 * lines land under the other's.
 */
export const formatBarLines = (lines, beatsPerBar, sections) => {
  const normalized = normalizeGrid(lines, sections);
  const grid = normalized.lines.map((cells) =>
    // Through cellChordBeats rather than parseCell: it is the one reading of a cell, and having the
    // writer go out the same way everything else comes in is what stops the two drifting apart.
    cells.map((cell) => keepMelody(cell, formatCell(cellChordBeats(cell, beatsPerBar), beatsPerBar))),
  );

  if (grid.length === 0) return '';

  const width = Math.max(1, ...grid.flat().map((cell) => cell.length));

  return grid
    .map((cells, index) => {
      const row = `${BAR} ${cells.map((cell) => cell.padEnd(width)).join(` ${BAR} `)} ${BAR}`;

      return index > 0 && normalized.sections[index] > normalized.sections[index - 1] ? `\n${row}` : row;
    })
    .join('\n');
};

/**
 * Bars in the grid — the same number countBars will read back off formatBarLines' output.
 *
 * Sections are sequential, so a sectioned chart counts exactly what the same lines counted before
 * they were divided: the blank row the break is written as is not a bar line, and countBars never
 * sees it.
 */
export const countGridBars = (lines, sections) =>
  normalizeGrid(lines, sections).lines.reduce((total, cells) => total + cells.length, 0);

/**
 * The bar each section opens on, counted from the top of the chart.
 *
 * A sectioned chart is one timeline — the sections run one after another — so the bar a section
 * starts on is simply everything written above it. What reads it is the melody: a clip played for
 * the second instrument in a chart belongs where that instrument comes in, not at bar 1.
 */
export const getSectionStartBars = (lines, sections) => {
  const starts = [];
  let bar = 0;

  (lines || []).forEach((cells, line) => {
    if (line === 0 || opensSection(sections, line)) starts.push(bar);

    bar += (cells || []).length;
  });

  return starts;
};

/**
 * One cell read as what it is: chords, and how many beats of the bar each was written across.
 *
 * `slots` is null for a cell written without beat marks — the whole of the notation as it stood
 * before them, and still how nearly every bar is written. Null is the signal to fall through to
 * splitBeats rather than a count of anything, so a bar nobody marked up is read exactly as it
 * always was.
 *
 * @returns {Array<{ chord: string, slots: number|null }>}
 */
export const parseCell = (cell) => {
  const text = cellBody(cell);

  if (text === '') return [];

  const tokens = text.split(/\s+/);
  const marked = tokens.some((token) => isBeatMark(token) || isMissingMark(token));
  const entries = [];
  let previous = null;

  tokens.forEach((token) => {
    if (!isMissingMark(token)) previous = token;

    if (isRestMark(token)) {
      entries.push({ chord: null, slots: 1, rest: true });

      return;
    }

    // A run of marks is one gap, and so is a mark carried on by beat marks the way a chord is.
    // A mark after a beat mark opens a gap of its own, which is how two side by side stay two.
    if (isMissingMark(token)) {
      const last = entries[entries.length - 1];

      if (last?.missing && isMissingMark(previous)) last.slots += 1;
      else entries.push({ chord: null, slots: 1, missing: true });

      previous = token;

      return;
    }

    // A mark extends the chord in front of it — or, opening the cell, opens a tie: a run of beats
    // the bar spends on whatever was still sounding behind it, with no symbol of its own.
    if (isBeatMark(token)) {
      if (entries.length > 0) entries[entries.length - 1].slots += 1;
      else entries.push({ chord: null, slots: 1 });

      return;
    }

    entries.push({ chord: token, slots: 1 });
  });

  return marked ? entries : entries.map(({ chord, rest }) => ({ chord, slots: null, ...(rest ? { rest } : null) }));
};

/** The chords written into one cell, without the beats they are held over. An empty cell has none. */
export const cellChords = (cell) =>
  parseCell(cell)
    // A tie is not a change: it carries no symbol, and everything that counts the chords in a bar —
    // hasChord, normalizeGrid's merging, isShared, the chart's widths — means the symbols
    .filter(({ chord }) => chord !== null)
    .map(({ chord }) => chord);

/** The beats a marked-up cell is written across — null for one written without marks. */
export const cellSlots = (cell) => {
  const entries = parseCell(cell);

  return entries.length > 0 && entries[0].slots !== null ? entries.reduce((total, { slots }) => total + slots, 0) : null;
};

/**
 * How many beats each of `count` chords sharing `totalBeats` gets.
 *
 * Even division wherever it divides — two chords in 4/4 are halves, four are a click each. Where it
 * doesn't, the chords land on clicks and the last one holds what is left over: a chord written into
 * a bar is played on a beat, not at some fraction between two, so three in 4/4 is 1-1-2 rather than
 * 1⅓ each. More chords than beats has no click to land on, so it splits evenly and the caller gets
 * fractions — which is the honest reading of a bar written with more changes than it has beats.
 *
 * @returns {number[]} One beat length per chord, summing to totalBeats
 */
export const splitBeats = (count, totalBeats) => {
  const n = Math.max(1, Math.floor(count));
  const beats = Number(totalBeats) || 0;
  const each = Math.floor(beats / n);

  if (each < 1) return Array.from({ length: n }, () => beats / n);

  // Whatever the floor left over goes to the last chord, which is the one still sounding at the
  // bar line — hanging it on any of the others would move a change off its click
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? beats - each * (n - 1) : each));
};

// A bar's beats can be shared out unevenly — cellChordBeats below divides a bar by however many
// slots were written into it — so a span's length is an arbitrary fraction and comparing it to a
// whole number of beats has to leave room for the float that comes back. Exported because everyone
// downstream doing beat arithmetic on what cellChordBeats hands back needs the same tolerance.
export const BEAT_EPSILON = 1e-6;

/**
 * One bar's chords, each with the beats it holds — what the builder and the chart both draw from.
 *
 * Two readings, and which one applies is written into the cell itself. A bar carrying beat marks
 * says outright where its changes land, so it is read off the marks. A bar without them says only
 * how many changes share it, which is splitBeats' question and is answered exactly as it was
 * before any of this existed.
 *
 * `beats` is null only when there is nothing to divide: no marks, no time signature, or a single
 * chord taking the whole bar however long that is. Callers use it as a flex-grow, so a null leaves
 * the layout to the stylesheet's even spread rather than sizing anything.
 *
 * A mark is a beat, which is why a marked bar needs no time signature to be spaced correctly — and
 * why a bar carrying fewer marks than it has beats is short of a beat rather than stretched over
 * one, so the builder can draw the room it still has. The exception is a bar written finer than its
 * own beats, an eighth grid in 4/4 say, where the marks are scaled onto the bar instead of
 * overflowing it: eight of them across four beats is half a beat each, which is what was written.
 *
 * @returns {Array<{ chord: string, beats: number|null, slots: number|null }>}
 */
export const cellChordBeats = (cell, beatsPerBar) => {
  const entries = parseCell(cell);
  const bar = Number(beatsPerBar) > 0 ? Number(beatsPerBar) : null;
  const marked = entries.length > 0 && entries[0].slots !== null;

  if (marked) {
    const total = entries.reduce((sum, { slots }) => sum + slots, 0);
    const perSlot = bar && total > bar ? bar / total : 1;

    return entries.map(({ chord, slots, missing, rest }) => ({
      chord,
      beats: slots * perSlot,
      slots,
      ...(missing ? { missing } : null),
      ...(rest ? { rest } : null),
    }));
  }

  const beats = entries.length > 1 && bar ? splitBeats(entries.length, bar) : null;

  return entries.map(({ chord, rest }, index) => ({
    chord,
    beats: beats ? beats[index] : null,
    slots: null,
    ...(rest ? { rest } : null),
  }));
};

/** Beats of a bar still left unspecified — what the builder warns about and won't save. */
export const cellMissingBeats = (cell, beatsPerBar) =>
  cellChordBeats(cell, beatsPerBar).reduce((total, { missing, beats }) => total + (missing ? beats : 0), 0);

/**
 * One cell as it will be written — the other direction of parseCell, and where "marks only for an
 * uneven bar" is decided.
 *
 * The marks exist to say something the bare chords cannot. Where they would only repeat what
 * splitBeats already reads off the count, they are left out: the notation stays the notation
 * everyone has been writing, and a chart written before any of this round-trips through the
 * builder untouched. So "C G Am" in 4/4 stays "C G Am" — splitBeats calls that 1+1+2, which is
 * exactly what the builder wrote — while three beats then one becomes "C / / G".
 *
 * A bar left part-written hands its unclaimed beats to the last chord, which is the rule splitBeats
 * already uses for a remainder and the rule a hold uses for the bars after it. One chord in a bar
 * therefore always writes bare, however few beats it was given: there is nothing after it to take
 * the rest.
 */
const withMarks = (chords, slots) =>
  chords
    .map((chord, index) =>
      // A tie is marks all the way: no symbol, because the chord it is holding was written in the
      // bar before it
      chord === null
        ? Array(Math.max(0, slots[index])).fill(BEAT_MARK).join(' ')
        : [chord, ...Array(Math.max(0, slots[index] - 1)).fill(BEAT_MARK)].join(' '),
    )
    .join(' ');

// An entry as withMarks takes it: missing beats and rests go in as their own marks
const markToken = ({ chord, missing, rest }) => {
  if (missing) return MISSING_MARK;

  return rest ? REST_MARK : chord;
};

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

/**
 * The coarsest grid a full bar can be written on without changing what it says.
 *
 * A bar written on a finer grid than it needs is correct but unreadable: two chords sharing a 4/4
 * bar down the middle is "C / / / G / / /" on the eighth grid and "C G" on the quarter, and those
 * are the same bar. Dividing the marks through by what they have in common is what picks the second.
 *
 * The floor is the part that is easy to leave out and impossible to leave out safely. cellChordBeats
 * only scales the marks onto the bar when there are *more* of them than the bar has beats; below
 * that a mark is a beat flat, and the bar is read as one somebody left part-written. So reducing
 * past `beatsPerBar` doesn't tidy a bar, it re-times it — three chords across a 4/4 bar in triplets
 * are twelve slots of [4,4,4], and dividing those all the way through by four leaves [1,1,1] of
 * three, which the pad below then turns into [1,1,2]. Stopping at [2,2,2] of six keeps the bar
 * saying what it said.
 */
/**
 * The coarsest grid every one of these lengths lands squarely on.
 *
 * The other direction of reduceSlots, and what lets a bar be edited in whole numbers: pick the grid
 * first, count in slots of it, and the arithmetic that follows never sees a fraction. Halves land on
 * the eighth grid, thirds on the triplet one, and a bar already carrying either drags the write onto
 * the same grid by being fed in alongside the length being asked for.
 *
 * The epsilon is not decoration — a third of a beat has no exact binary form, so three of them only
 * add back up to one within a rounding error. Falling off the end of the list means a length no
 * musical grid describes, which nothing in the builder can ask for; the finest grid and a rounding
 * is the honest answer for a bar somebody typed by hand.
 */
const SUBDIVISIONS = [1, 2, 3, 4, 6, 8, 12, 16];

export const subdivisionFor = (beats) =>
  SUBDIVISIONS.find((d) => beats.every((held) => Math.abs(held * d - Math.round(held * d)) < 1e-6)) ??
  SUBDIVISIONS[SUBDIVISIONS.length - 1];

const reduceSlots = (slots, capacityInBeats) => {
  const total = slots.reduce((sum, held) => sum + held, 0);
  const shared = slots.reduce(gcd);

  let coarsest = 1;

  // Every divisor of the gcd divides the total too, so this only ever asks whether the grid it
  // leaves behind is still at least as fine as the bar's own beats
  for (let d = 2; d <= shared; d += 1) {
    if (shared % d === 0 && total / d >= capacityInBeats) coarsest = d;
  }

  return coarsest === 1 ? slots : slots.map((held) => held / coarsest);
};

export const formatCell = (entries, beatsPerBar) => {
  const cells = (entries || []).filter(({ chord }) => chord === null || String(chord).trim() !== '');

  if (cells.length === 0) return '';

  const chords = cells.map(markToken);
  const bar = Number(beatsPerBar) > 0 ? Number(beatsPerBar) : null;
  const marked = cells.every(({ slots }) => Number(slots) > 0);
  // A tie can only ever be said with marks — it has no symbol to say it with — so none of the
  // shortcuts below are open to a bar carrying one. A rest has a symbol, so it doesn't count.
  const tied = cells.some(({ chord, rest }) => chord === null && !rest);

  // Nothing to write down: a bar with no lengths in it, or one chord, which holds the bar whatever
  // it was written at
  if (!tied && (!marked || cells.length === 1)) return chords.join(' ');

  const slots = cells.map(({ slots: held }) => held);

  // No time signature to judge against, so no bar can be called even and nothing can be dropped.
  // Writing the marks back out is the only answer that doesn't lose what the cell said.
  if (!bar) return withMarks(chords, slots);

  // Whatever is left of the bar goes to the chord still sounding at the bar line. A bar already
  // written on a finer grid has more slots than the bar has beats, so this adds nothing to it —
  // the room it would be filling is room the finer grid has already spoken for.
  slots[slots.length - 1] += Math.max(0, bar - slots.reduce((total, held) => total + held, 0));

  const reduced = reduceSlots(slots, bar);
  // A gap one slot long is its mark alone, which would run into a gap straight after it. A grid twice
  // as fine gives it a beat mark to end on.
  const crowded = reduced.some((held, i) => cells[i].missing && held === 1 && cells[i + 1]?.missing);
  const written = crowded ? reduced.map((held) => held * 2) : reduced;
  const even = splitBeats(cells.length, bar);

  // Only a bar that reduced onto its own beats can have its marks dropped: that is the one grid
  // splitBeats speaks for, and the reading a bare row of chords gets back. A finer bar keeps them.
  // No need to check that separately — a finer bar's marks total more than the bar has beats, and
  // splitBeats' always total exactly the bar, so the two can never match element for element.
  return !tied && written.every((held, index) => held === even[index]) ? chords.join(' ') : withMarks(chords, written);
};

/* ---------------------------------------------------------------------------
 * Grid editing
 *
 * Pure, so the modal holds one piece of state and no logic. Every function returns a new grid and
 * leaves its argument alone.
 * ------------------------------------------------------------------------- */

export const DEFAULT_BARS_PER_LINE = 4;

// The most bars a line holds in the builder. Past four they get too narrow to read on a phone.
export const MAX_BARS_PER_LINE = 4;

export const createGrid = (lineCount = 1, barsPerLine = DEFAULT_BARS_PER_LINE) =>
  Array.from({ length: lineCount }, () => Array.from({ length: barsPerLine }, () => ''));

const replaceLine = (lines, index, cells) => lines.map((line, i) => (i === index ? cells : line));

// A line cut into runs of MAX_BARS_PER_LINE bars. An empty line stays one empty line.
const chunkLine = (cells) =>
  Array.from({ length: Math.max(1, Math.ceil(cells.length / MAX_BARS_PER_LINE)) }, (_, i) =>
    cells.slice(i * MAX_BARS_PER_LINE, (i + 1) * MAX_BARS_PER_LINE),
  );

/**
 * A grid with every line longer than MAX_BARS_PER_LINE split into lines that aren't, each one in
 * the section of the line it came from. For charts written before the limit, or pasted in.
 */
export const wrapLines = (lines, sections) => {
  const chunks = lines.map(chunkLine);

  return {
    lines: chunks.flat(),
    sections: normalizeSections(chunks.flatMap((rows, i) => rows.map(() => sections?.[i] ?? 0))),
  };
};

// How many chords make a bar busy enough to take room from the held bars beside it
export const BUSY_BAR_CHORDS = 3;

/**
 * Whether a chart's held bars should be drawn narrow so its busy bars can widen. Only on a
 * one-line chart: bars share one width so their lines align row to row, and with one row there is
 * nothing to align with. Needs a held bar (or room left for fillers) and a non-melody bar holding
 * BUSY_BAR_CHORDS or more chords. Takes wrapped lines (see wrapLines).
 */
export const narrowsHolds = (lines) => {
  if (lines?.length !== 1) return false;

  const [cells] = lines;
  const hasHold = cells.length < DEFAULT_BARS_PER_LINE || cells.some((cell) => cell === '');
  const isBusy = (cell) =>
    !isMelodyCell(cell) &&
    cellChordBeats(cell).filter(({ chord, rest }) => chord !== null && !rest).length >= BUSY_BAR_CHORDS;

  return hasHold && cells.some(isBusy);
};

/**
 * Where the cursor goes once a bar is done with: along the line, or onto the next one — adding it,
 * as wide as this one, when there isn't one.
 */
const stepAfter = (grid, cells, line, index) => {
  if (index + 1 < cells.length) return { lines: grid, position: { line, bar: index + 1 } };

  if (line + 1 < grid.length) return { lines: grid, position: { line: line + 1, bar: 0 } };

  return { lines: [...grid, createGrid(1, cells.length)[0]], position: { line: line + 1, bar: 0 } };
};

/**
 * Write a chord at a position and hold it for `length` bars.
 *
 * The bars it is held over are cleared rather than repeated — a repeated chord symbol reads as a
 * restrike, and an empty bar is how a chart says the chord is still sounding. A chord that runs
 * past the end of its line grows the line up to MAX_BARS_PER_LINE, then carries on over the next
 * line's bars, rather than being cut short.
 *
 * @returns {{ lines: string[][], position: {line: number, bar: number} }} The grid, and where the
 *   cursor lands: the bar after the chord, on a new line's first bar when it filled the last one.
 */
export const writeChord = (lines, position, chord, length = 1) => {
  const { line, bar } = position;
  const held = Math.max(1, length);
  const cells = [...(lines[line] || [])];

  while (cells.length <= bar || cells.length < Math.min(bar + held, MAX_BARS_PER_LINE)) cells.push('');

  // The bars of this line the chord takes; the rest of it goes over the lines after
  const here = Math.max(1, Math.min(held, cells.length - bar));

  // A melody bar stays one: the chord written over it is its note
  cells[bar] = keepMelody(cells[bar], String(chord ?? '').trim());
  for (let i = 1; i < here; i += 1) cells[bar + i] = '';

  let state = stepAfter(replaceLine(lines, line, cells), cells, line, bar + here - 1);

  for (let i = here; i < held; i += 1) {
    const at = state.position;
    const row = [...(state.lines[at.line] || [])];

    while (row.length <= at.bar) row.push('');
    row[at.bar] = '';

    state = stepAfter(replaceLine(state.lines, at.line, row), row, at.line, at.bar);
  }

  return state;
};

/**
 * The chord sounding in one bar: the chord written there, or — when the bar is a hold — whichever
 * chord before it is still ringing.
 *
 * Walks the raw grid rather than normalizeGrid's, because a position addresses the grid as the
 * editor draws it and normalizing merges hold-only lines into their neighbour, moving the indices.
 *
 * The last chord of a bar, not the bar's text: what is still sounding at the end of "C / / G" is G,
 * and what is still sounding at the end of "Em C" is C. Callers hand this straight to the piano and
 * to getChordMidiNotes, neither of which can make anything of a whole bar's worth of notation.
 */
export const getSoundingChord = (lines, { line, bar }) => {
  let sounding = '';

  for (let l = 0; l < (lines || []).length; l += 1) {
    const cells = lines[l] || [];

    for (let b = 0; b < cells.length; b += 1) {
      // A rest stops whatever was sounding; a tie leaves it be
      for (const { chord, rest } of parseCell(cells[b])) {
        if (chord !== null) sounding = chord;
        else if (rest) sounding = '';
      }

      if (l === line && b === bar) return sounding;
    }
  }

  // Ran off the end of the grid, so whatever was written last is what is still holding
  return sounding;
};

/** A bar's entries written out as marks, one slot each unless they say otherwise. */
const serializeEntries = (entries) =>
  withMarks(
    entries.map(markToken),
    entries.map(({ slots }) => slots ?? 1),
  );

/**
 * Move one chord to another place in its own bar.
 *
 * A chord carries the beats it was written for. Drag the three-beat chord of a bar to the front and
 * three beats is what stands at the front — the box that moved is the width it was, which is the
 * only reading that matches what the drag looked like.
 *
 * Read through cellChordBeats rather than off the marks, so a bar written without them reorders
 * truthfully too: "C G Am" in 4/4 is 1+1+2 by splitBeats, and moving Am to the front has to write
 * "Am / C G" rather than "Am C G", which would silently hand the two beats to G instead. It goes
 * back out through reduceSlots, so a bar that no longer needs its marks loses them again.
 *
 * A tie is not a chord and cannot be dragged, nor anything dragged in front of it: it is the bar
 * opening on what was already sounding, and there is nothing to sound before the bar line.
 */
export const reorderChord = (lines, { line, bar }, from, to, beatsPerBar = 4) => {
  const cells = lines[line] || [];
  const held = cellChordBeats(cells[bar], beatsPerBar);
  const first = held[0]?.chord === null && !held[0].rest ? 1 : 0;

  if (from === to || from < first || to < first || from >= held.length || to >= held.length) return lines;
  // Missing beats stay at the end of the bar, where the room is
  if (held[from].missing || held[to].missing) return lines;

  const beats = held.map(({ beats: b }) => b ?? beatsPerBar);
  const grain = subdivisionFor(beats);
  const entries = held.map(({ chord, missing, rest }, i) => ({ chord, missing, rest, slots: Math.round(beats[i] * grain) }));

  const [moved] = entries.splice(from, 1);
  entries.splice(to, 0, moved);

  const written = reduceSlots(
    entries.map(({ slots }) => slots),
    beatsPerBar,
  );

  const text = keepMelody(
    cells[bar],
    serializeEntries(entries.map(({ chord, missing, rest }, i) => ({ chord, missing, rest, slots: written[i] }))),
  );

  return replaceLine(
    lines,
    line,
    cells.map((cell, i) => (i === bar ? text : cell)),
  );
};

/** Exchange two bars as written, anywhere in the grid. */
export const swapBars = (lines, a, b) => {
  const first = lines[a.line]?.[a.bar];
  const second = lines[b.line]?.[b.bar];

  if (first === undefined || second === undefined || (a.line === b.line && a.bar === b.bar)) return lines;

  return lines.map((cells, l) =>
    l !== a.line && l !== b.line
      ? cells
      : cells.map((cell, i) => {
          if (l === a.line && i === a.bar) return second;
          if (l === b.line && i === b.bar) return first;

          return cell;
        }),
  );
};

/**
 * A part's written chords as a grid to start a chart from: one grid line per written line, one bar
 * per chord. Carries the rows they came from, so inserting the chart can replace them.
 *
 * Null when there is nothing to take, or when words sit between the chord lines — replacing that run
 * would take the words with it. Lines above or below the run (a repeat directive, a note) are kept.
 *
 * @param {object} [options]
 * @param {boolean} [options.interleaved] - Take the chord lines even with words between them, which
 *   is how a verse writes them: chords on one row, the words they sit over on the next. Only safe
 *   for a chart stored off the lyrics, because `startLine`/`endLine` then span those words too —
 *   a caller that splices over that run would delete them.
 */
export const findChordLines = (lyrics, { interleaved = false } = {}) => {
  const rows = String(lyrics ?? '').split(/\r\n|\r|\n/);
  const found = rows
    .map((row, index) => ({
      index,
      chords: findChords(row, { display: true }).filter(isChordToken),
    }))
    .filter(({ chords }) => chords.length > 0);

  if (found.length === 0) return null;

  const startLine = found[0].index;
  const endLine = found[found.length - 1].index;

  if (!interleaved && rows.slice(startLine, endLine + 1).some(isLyricsLine)) return null;

  return { lines: found.map(({ chords }) => chords), startLine, endLine };
};

/**
 * Move a bar, as written, to the end of a line. A line it leaves is never emptied, and a line
 * already holding MAX_BARS_PER_LINE takes no more.
 */
export const moveBar = (lines, from, toLine) => {
  const cell = lines[from.line]?.[from.bar];
  const isOtherLine = from.line !== toLine;

  if (cell === undefined || !lines[toLine]) return lines;
  if (isOtherLine && (lines[from.line].length <= 1 || lines[toLine].length >= MAX_BARS_PER_LINE)) return lines;

  return lines.map((cells, l) => {
    const kept = l === from.line ? cells.filter((_, i) => i !== from.bar) : cells;

    return l === toLine ? [...kept, cell] : kept;
  });
};

/**
 * Take one chord out of a bar. The chords behind it slide up, and the beats it held are left missing
 * at the end of the bar, so nothing else in the line changes length — the last chord out included,
 * which leaves the bar missing rather than a hold that would lengthen the chord before it.
 */
export const removeChord = (lines, { line, bar }, index, beatsPerBar = 4) => {
  const cells = lines[line] || [];
  const held = cellChordBeats(cells[bar], beatsPerBar);

  if (!held[index] || held[index].chord === null) return lines;

  const beats = held.map(({ beats: b }) => b ?? beatsPerBar);
  const grain = subdivisionFor(beats);
  const entries = held.map(({ chord, missing, rest }, i) => ({ chord, missing, rest, slots: Math.round(beats[i] * grain) }));
  const [removed] = entries.splice(index, 1);

  // A bar written short of its beats is short of them here too, so they go missing along with the rest
  const written = entries.reduce((sum, { slots }) => sum + slots, removed.slots);
  const freed = removed.slots + Math.max(0, beatsPerBar * grain - written);
  const last = entries[entries.length - 1];

  if (last?.missing) last.slots += freed;
  else entries.push({ chord: null, missing: true, slots: freed });

  const text = keepMelody(
    cells[bar],
    withMarks(
      entries.map(markToken),
      reduceSlots(
        entries.map(({ slots }) => slots),
        beatsPerBar,
      ),
    ),
  );

  return replaceLine(lines, line, cells.map((cell, i) => (i === bar ? text : cell)));
};

/**
 * Write a chord into a run of missing beats, taking the whole run.
 *
 * A gap is the bar saying it has beats nobody has written yet, so there is nothing in it to share
 * with: a chord that took only part of one would leave a gap on either side of itself that nobody
 * asked for. Taking all of it is also what "move it there" means from the grid — the chord lands
 * where the gap was and lasts as long as it did.
 */
export const fillGap = (lines, { line, bar, index }, chord, beatsPerBar = 4) => {
  const name = String(chord ?? '').trim();
  const cells = lines[line] || [];
  const held = cellChordBeats(cells[bar], beatsPerBar);

  if (!name || !held[index]?.missing) return lines;

  const beats = held.map(({ beats: b }) => b ?? beatsPerBar);
  const grain = subdivisionFor(beats);
  const entries = held.map(({ chord: written, missing, rest }, i) => ({
    chord: written,
    missing,
    rest,
    slots: Math.round(beats[i] * grain),
  }));

  entries[index] = { chord: name, missing: false, slots: entries[index].slots };

  const text = keepMelody(
    cells[bar],
    withMarks(
      entries.map(markToken),
      reduceSlots(
        entries.map(({ slots }) => slots),
        beatsPerBar,
      ),
    ),
  );

  return replaceLine(
    lines,
    line,
    cells.map((cell, i) => (i === bar ? text : cell)),
  );
};

/**
 * Move a written chord into a run of missing beats, anywhere in the chart.
 *
 * What it leaves behind is a gap of its own: the bar it came from keeps its length, and the beats it
 * gave up go missing exactly as removeChord leaves them. So a move trades one gap for another rather
 * than changing anything's length — the one thing that was asked for, and nothing else.
 *
 * Filling before removing is what keeps `from` pointing at the right chord. A gap becomes a chord
 * where it stood, so no entry moves and no two runs of missing beats can merge; removing first would
 * shift every entry after the hole, and `from` would be an index into a bar that no longer exists.
 */
export const moveChord = (lines, from, to, beatsPerBar = 4) => {
  const cells = lines[from.line] || [];
  const name = cellChordBeats(cells[from.bar], beatsPerBar)[from.index]?.chord;

  if (!name) return lines;

  const filled = fillGap(lines, to, name, beatsPerBar);

  // The target wasn't a gap after all, so nothing moves rather than the chord being written twice
  if (filled === lines) return lines;

  return removeChord(filled, { line: from.line, bar: from.bar }, from.index, beatsPerBar);
};

export const clearCell = (lines, { line, bar }) =>
  replaceLine(
    lines,
    line,
    (lines[line] || []).map((cell, i) => (i === bar ? '' : cell)),
  );

/** One more bar at the end of a line, unless it already has MAX_BARS_PER_LINE. */
export const addBar = (lines, line) =>
  (lines[line]?.length ?? 0) >= MAX_BARS_PER_LINE ? lines : replaceLine(lines, line, [...(lines[line] || []), '']);

/**
 * A line brought up to MAX_BARS_PER_LINE with empty bars, put in after the given bar, or at the end
 * when none is given.
 */
export const fillLine = (lines, line, after) => {
  const cells = lines[line] || [];
  const at = (after ?? cells.length - 1) + 1;
  const added = Array.from({ length: Math.max(MAX_BARS_PER_LINE - cells.length, 0) }, () => '');

  return added.length === 0 ? lines : replaceLine(lines, line, [...cells.slice(0, at), ...added, ...cells.slice(at)]);
};

/** Every line brought up to MAX_BARS_PER_LINE with empty bars at its end. */
export const fillLines = (lines) =>
  lines.some((cells) => cells.length < MAX_BARS_PER_LINE) ? lines.map((_, line) => fillLine(lines, line)[line]) : lines;

/** Take one bar out of a line: the given one, or the last when none is given. */
export const removeBar = (lines, line, bar) => {
  const cells = lines[line] || [];
  const index = bar ?? cells.length - 1;

  return replaceLine(
    lines,
    line,
    cells.filter((_, i) => i !== index),
  );
};

export const addLine = (lines, barsPerLine = DEFAULT_BARS_PER_LINE) => [...lines, createGrid(1, barsPerLine)[0]];

/** An empty line of `barsPerLine` bars, dropped in directly below the given line. */
export const insertLine = (lines, line, barsPerLine = DEFAULT_BARS_PER_LINE) => [
  ...lines.slice(0, line + 1),
  createGrid(1, barsPerLine)[0],
  ...lines.slice(line + 1),
];

export const removeLine = (lines, line) => lines.filter((_, i) => i !== line);

/**
 * Every line with nothing written in it taken out, with the sections closing up around them. A chart
 * that is all empty keeps its first line, so there is still somewhere to write. Unchanged when there
 * is nothing to take out.
 */
export const removeEmptyLines = (lines, sections) => {
  const isEmpty = (cells) => cells.every((cell) => cell === '');
  const keep = lines.map((cells, line) => !isEmpty(cells) || (line === 0 && lines.every(isEmpty)));

  if (keep.every(Boolean)) return { lines, sections };

  return {
    lines: lines.filter((_, line) => keep[line]),
    sections: normalizeSections((sections || []).filter((_, line) => keep[line])),
  };
};

/** A copy of one line, dropped in directly below the line it copies. */
export const duplicateLine = (lines, line) =>
  lines[line] ? [...lines.slice(0, line + 1), [...lines[line]], ...lines.slice(line + 1)] : lines;

/**
 * Move one line to another index, the way dragging it does: `to` is where it ends up in the
 * finished list, so everything it passes shuffles the other way to make room.
 */
export const moveLine = (lines, from, to) => {
  if (from === to || !lines[from]) return lines;

  const rest = lines.filter((_, i) => i !== from);

  return [...rest.slice(0, to), lines[from], ...rest.slice(to)];
};

/**
 * Where an index ends up once moveLine has shuffled the lines around it — how the cursor keeps
 * hold of the line it was on while the chart is reordered underneath it.
 */
export const lineAfterMove = (index, from, to) => {
  if (index === from) return to;
  if (from < index && index <= to) return index - 1;
  if (to <= index && index < from) return index + 1;

  return index;
};
